import React from 'react';

// The main student app hosts the real user/access admin (it owns the Supabase
// SDK, the is-banned middleware mirror, the delete-cascade, and the right auth
// model). Per the OQ-2 decision, the console DEEP-LINKS to it rather than
// re-porting (~1,700 LOC) or relocating a Supabase service key here.
//
// Resolve the deployed main-app origin (e.g. https://myacademicplanner.com).
// Prefer the explicit build-env var; if it's unset, derive it from the admin
// host (admin.<domain> -> <domain>) so a forgotten Pages env var degrades to a
// best-effort real origin rather than a dead localhost:3000 link in production.
// Falls back to the local dev server only on localhost.
function resolveMainAppUrl() {
  const fromEnv = process.env.REACT_APP_MAIN_APP_URL;
  if (fromEnv) return fromEnv.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, host } = window.location;
    if (host && !host.startsWith('localhost') && !host.startsWith('127.')) {
      return `${protocol}//${host.replace(/^admin\./, '')}`;
    }
  }
  return 'http://localhost:3000';
}

const MAIN_APP_URL = resolveMainAppUrl();

const LINKS = [
  { label: 'Dashboard', path: '/admin', desc: 'Admin overview' },
  { label: 'Users', path: '/admin/users', desc: 'View / manage users, roles, bans' },
  { label: 'Invite Codes', path: '/admin/invite-codes', desc: 'Generate / manage invite codes' },
  { label: 'Email Invitations', path: '/admin/email-invitations', desc: 'Pre-register emails (LMS-style)' },
  { label: 'Feedback', path: '/admin/feedback', desc: 'User-submitted feedback' },
  { label: 'Activity', path: '/admin/activity', desc: 'User activity log' },
  { label: 'Chats', path: '/admin/chats', desc: 'Chat conversations' },
];

function UserAccess() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Users &amp; Access</h2>
        <p className="text-sm text-gray-500">
          User and access management lives in the main app's admin section. These open it in a new tab.
          Institution scoping is handled there by the signed-in admin's active institution.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {LINKS.map((l) => (
          <a
            key={l.path}
            href={`${MAIN_APP_URL}${l.path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white rounded-lg border p-4 hover:border-blue-400 hover:shadow-sm transition"
          >
            <div className="text-sm font-semibold text-blue-700">
              {l.label} <span aria-hidden="true">↗</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">{l.desc}</div>
          </a>
        ))}
      </div>

      <p className="text-xs text-gray-400">
        Opens <span className="font-mono">{MAIN_APP_URL}/admin/*</span> — requires a main-app admin login.
      </p>
    </div>
  );
}

export default UserAccess;
